const initMap = async map => {
	const d3 = await import('d3');
	const topojson = await import('topojson-client');
	const simplify = await import('topojson-simplify');
	const mapData = await d3.json('/map.json');
	const { computePosition, flip, arrow, offset, shift } = await import(
		'@floating-ui/dom'
	);
	const width = map.offsetWidth;
	const height = map.offsetHeight;

	// Simplify Map Data
	const simplifiedMapData = simplify.simplify(
		simplify.presimplify(mapData),
		0.04,
	);
	const geojson = topojson.feature(
		simplifiedMapData,
		simplifiedMapData.objects.CNTR_RG_10M_2024_4326,
	);
	type City = {
		title: string;
		numberOfArtworks: number;
		numberOfArtists: number;
		showTitle: boolean;
		coords: {
			lat: number;
			long: number;
		};
	};

	type Country = {
		id: string;
		title: string;
		numberOfArtworks: number;
		numberOfArtists: number;
		offsetX?: number;
		offsetY?: number;
		cities: City[];
	};

	const countries = JSON.parse(map.dataset.map);

	const zoom = d3.zoom().on('zoom', zoomed);

	const svg = d3
		.select(map)
		.style('position', 'relative')
		.style('overflow', 'hidden')
		.append('svg')
		.attr('viewBox', `0 0 ${width} ${height}`)
		.attr('width', '100%')
		.attr('height', '100%')
		.attr('style', 'width: 100%; height: auto;');

	const collection = {
		type: 'FeatureCollection',
		features: geojson.features.filter(f =>
			countries.map(c => c.id).includes(f.id),
		),
	};
	const projection = d3.geoMercator();

	const path = d3.geoPath().projection(projection);

	// Add custom properties to GeoJSON data. Used in SVG.
	countries.forEach(country => {
		const feature = geojson.features.find(f => f.id === country.id);
		if (feature) {
			const centroid = path.centroid(feature);
			const centerX = centroid[0] + (country.offsetX || 0);
			const centerY = centroid[1] + (country.offsetY || 0);

			feature.properties = {
				...feature.properties,
				...country,
				center: [centerX, centerY],
				radius: Math.max(1, Math.min(2.5, country.numberOfArtworks / 800)),
			};
		}
	});

	const centroids = {
		type: 'FeatureCollection',
		features: collection.features.map(c => ({
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: c.properties.center,
			},
		})),
	};
	svg
		.append('rect')
		.attr('x', 0)
		.attr('y', 0)
		.attr('width', width)
		.attr('height', height)
		.attr('fill', 'var(--color-mid)')
		.on('click', () => reset(750));

	const g = svg.append('g');

	const root = g
		.append('g')
		.selectAll('path')
		.data(geojson.features)
		.join('path')
		.attr('fill', d => {
			return d.properties.id ? 'var(--color-black)' : 'url(#pattern-dots)';
		})
		.attr('cursor', d => (d.properties.cities?.length ? 'pointer' : null))
		.attr('tabindex', d => (d.properties.cities?.length ? '0' : null))
		.attr('aria-label', d =>
			d.properties.cities?.length ? d.properties.id : null,
		)
		.attr('stroke', 'white')
		.attr('stroke-width', '3')
		.attr('stroke-linejoin', 'round')
		.attr('vector-effect', 'non-scaling-stroke')
		.attr('d', path)
		.attr('id', d => (d.id ? `country-${d.id}` : null));

	if (!map.closest('a')) {
		root
			.on('click', clicked)
			.on('focus', clicked)
			.on('mouseenter', mouseenter)
			.on('mouseleave', mouseleave);
	}

	root.append('title').text(d => d.properties.NAME_ENGL);

	g.append('g')
		.attr('id', 'centers')
		.selectAll('circle')
		.data(collection.features)
		.enter()
		.append('circle')
		.attr('cursor', 'pointer')
		.attr('pointer-events', 'none')
		.attr('cx', f => f.properties.center[0])
		.attr('cy', f => f.properties.center[1])
		.attr('r', f => f.properties.radius)
		.attr('fill', 'var(--color-green)')
		.attr('id', f => `circle-${f.id}`);

	collection.features.forEach(country => {
		if (country.properties.cities) {
			g.append('g')
				.attr('id', `cities-${country.id}`)
				.attr('class', `cities`)
				.selectAll('circle')
				.data(country.properties.cities)
				.enter()
				.append('circle')
				.attr('cursor', 'pointer')
				.attr('cx', f => projection([f.coords.long, f.coords.lat])[0])
				.attr('cy', f => projection([f.coords.long, f.coords.lat])[1])
				.attr('r', 0.5)
				.attr('fill', 'var(--color-green)');
		}
	});

	const patternSize = 1;
	const patternRadius = patternSize / 5;

	const pattern = svg
		.append('pattern')
		.attr('id', 'pattern-dots')
		.attr('x', 0)
		.attr('y', 0)
		.attr('width', patternSize)
		.attr('height', patternSize)
		.attr('patternUnits', 'userSpaceOnUse');
	pattern.append('circle').attr('cx', 0).attr('cy', 0).attr('r', patternRadius);
	pattern
		.append('circle')
		.attr('cx', patternSize)
		.attr('cy', patternSize)
		.attr('r', patternRadius);
	pattern
		.append('circle')
		.attr('cx', patternSize / 2)
		.attr('cy', patternSize / 2)
		.attr('r', patternRadius);
	pattern
		.append('circle')
		.attr('cx', patternSize)
		.attr('cy', 0)
		.attr('r', patternRadius);
	pattern
		.append('circle')
		.attr('cx', 0)
		.attr('cy', patternSize)
		.attr('r', patternRadius);

	const pattern2 = svg
		.append('pattern')
		.attr('id', 'pattern-dots-semi')
		.attr('x', 0)
		.attr('y', 0)
		.attr('width', patternSize)
		.attr('height', patternSize)
		.attr('patternUnits', 'userSpaceOnUse');
	pattern2
		.append('path')
		.attr('d', `M0 0V${patternSize}H${patternSize}V-${patternSize}z`)
		.attr('fill', 'url(#pattern-dots)');
	pattern2
		.append('path')
		.attr('d', `M0 0V${patternSize}H${patternSize}V-${patternSize}z`)
		.attr('fill', 'rgb(0 0 0 / 0.7)');

	// Scale and translate to circles
	const reset = (duration = 0) => {
		svg.select('#centers').node().style.visibility = '';
		collection.features.forEach(f => {
			svg.select(`#country-${f.id}`).style('fill', 'var(--color-black)');
		});
		svg.selectAll('.cities').style('visibility', 'hidden');

		const [[x0, y0], [x1, y1]] = d3.geoBounds(centroids);
		svg
			.transition()
			.duration(duration)
			.call(
				zoom.transform,
				d3.zoomIdentity
					.translate(width / 2, height / 2)
					.scale(
						Math.min(18, 0.8 / Math.max((x1 - x0) / width, (y1 - y0) / height)),
					)
					.translate(-(x0 + x1) / 2, -(y0 + y1) / 2),
			);
		document.querySelector('[data-number-of-artworks]').innerText =
			initialNumberOfArtworks;
		document.querySelector('[data-number-of-artists]').innerText =
			initialNumberOfArtists;
		document.querySelector('[data-number-of-locations]').innerText =
			initialNumberOfLocations;
	};

	const initialNumberOfArtworks = document.querySelector(
		'[data-number-of-artworks]',
	).innerText;
	const initialNumberOfArtists = document.querySelector(
		'[data-number-of-artists]',
	).innerText;
	const initialNumberOfLocations = document.querySelector(
		'[data-number-of-locations]',
	).innerText;
	reset();

	const tooltip = d3
		.select(map)
		.append('div')
		.style('opacity', 0)
		.attr('class', 'p-s')
		.style('position', 'absolute')
		.style('transition', 'opacity 200ms ease-in-out')
		.style('top', 0)
		.style('left', 0)
		.style('pointer-events', 'none')
		.style('position', 'absolute')
		.style('width', 'max-content')
		.style('outline', '2px solid')
		.style('background-color', 'var(--color-green)');

	tooltip.append('div').attr('id', 'content');
	const arrowEl = tooltip
		.append('svg')
		.attr('id', 'arrow')
		.attr('viewBox', '0 0 20 20')
		.attr('width', '20')
		.attr('height', '20')
		.attr('aria-hidden', 'true')
		.style('position', 'absolute')
		.style('width', '20px')
		.style('height', '20px');

	arrowEl
		.append('path')
		.attr('fill', 'var(--color-green)')
		.attr('d', 'M0 0l20 0 L 0 20z');
	arrowEl
		.append('path')
		.attr('d', 'M0 0l20 0 L 0 20z')
		.attr('fill', 'none')
		.attr('stroke-width', '2')
		.attr('stroke', 'var(--color-black)')
		.attr('d', 'M1 0v19L 19 0');

	function mouseenter(event, d) {
		if (d.properties.numberOfArtworks) {
			const circle = svg.select(`#circle-${d.id}`).node();
			const circleWidth = circle.getBoundingClientRect().width;
			circle.style.visibility = 'hidden';

			if (this.tagName === 'path') {
				d3.select(this)
					.raise()
					.style('stroke', 'white')
					.style('stroke-width', '5');
			}

			tooltip
				.style('opacity', 1)
				.select('#content')
				.html(
					`<div class="heading-2">Strabag ${d.properties.id}</div>
					<div>${d.properties.numberOfArtworks} Artworks</div>`,
				);
			computePosition(circle, tooltip.node(), {
				placement: 'top-start',
				middleware: [
					offset({ mainAxis: 20 - circleWidth / 2 }),
					flip({ rootBoundary: map }),
					arrow({ element: tooltip.select('#arrow').node() }),
				],
			}).then(({ x, y, middlewareData }) => {
				const arrowEl = tooltip.select('#arrow').node();
				const flipInline = [1, 3].includes(middlewareData.flip?.index);
				const flipBlock = [2, 3].includes(middlewareData.flip?.index);

				Object.assign(arrowEl.style, {
					bottom: flipBlock ? '100%' : null,
					top: !flipBlock ? '100%' : null,
					right: flipInline ? '-2px' : null,
					left: !flipInline ? '-2px' : null,
					transform: `scale(${flipInline ? -1 : 1}, ${flipBlock ? -1 : 1})`,
				});

				Object.assign(tooltip.node().style, {
					transform: `translate3d(${(circleWidth / 2) * (flipInline ? -1 : 1)}px, 0, 0)`,
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		}
	}
	function mouseleave(event, d) {
		if (d.properties.numberOfArtworks) {
			d3.select(this).style('stroke', '').style('stroke-width', '');
			const circle = svg.select(`#circle-${d.id}`).node();

			if (circle) {
				circle.style.visibility = '';
			}
		}
		tooltip.style('opacity', 0);
	}

	let activeCountry: string | undefined;

	function clicked(event, d) {
		if (!d.properties.cities?.length || activeCountry === d.properties.id) {
			reset(750);
			activeCountry = undefined;
			return;
		}

		document.querySelector('[data-number-of-artworks]').innerText =
			d.properties.numberOfArtworks;
		document.querySelector('[data-number-of-artists]').innerText =
			d.properties.numberOfArtists;
		document.querySelector('[data-number-of-locations]').innerText =
			d.properties.cities.length;

		svg.select('#centers').node().style.visibility = 'hidden';

		collection.features.forEach(f => {
			svg
				.select(`#country-${f.id}`)
				.style('fill', f =>
					f.id === d.id ? 'var(--color-black)' : 'url(#pattern-dots)',
				);
		});

		svg.selectAll('.cities').style('visibility', 'hidden');

		svg.select(`#cities-${d.id}`).style('visibility', '');

		const [[x0, y0], [x1, y1]] = path.bounds(d);
		event.stopPropagation();
		svg
			.transition()
			.duration(750)
			.call(
				zoom.transform,
				d3.zoomIdentity
					.translate(width / 2, height / 2)
					.scale(0.8 / Math.max((x1 - x0) / width, (y1 - y0) / height))
					.translate(-(x0 + x1) / 2, -(y0 + y1) / 2),
				d3.pointer(event, svg.node()),
			);
		activeCountry = d.properties.id;
	}

	function zoomed(event) {
		const { transform } = event;
		g.attr('transform', transform);
		tooltip.style('opacity', 0);
	}
};

const map = document.querySelector('[data-map]');
if (map) {
	initMap(map);
}
